import React, { useEffect, useState } from 'react';

import type { WebSearchActivity, WebSearchSource } from '../../services/chatService';

import './WebSearchActivity.css';

const STATUS_LABEL: Record<WebSearchActivity['status'], string> = {
  searching: 'Searching the web for',
  done: 'Searched the web for',
  failed: 'Web search failed for',
  insufficient_credits: 'Not enough credits to search for',
};

const STATUS_ICON: Record<WebSearchActivity['status'], string> = {
  searching: 'fa-solid fa-spinner fa-spin',
  done: 'fa-solid fa-magnifying-glass',
  failed: 'fa-solid fa-triangle-exclamation',
  insufficient_credits: 'fa-solid fa-coins',
};

/** Site chips shown before the rest collapse into "+N". */
const MAX_SITES = 8;

const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

const formatCredits = (credits: number): string => {
  const rounded = Math.round(credits * 100) / 100;

  return `${rounded} ${rounded === 1 ? 'credit' : 'credits'}`;
};

/** One entry per site: the first source of each host, in the order the search returned them. */
export const siteSources = (sources: WebSearchSource[]): WebSearchSource[] => {
  const seen = new Set<string>();

  return sources.filter((source) => {
    const host = hostOf(source.url);

    if (seen.has(host)) return false;
    seen.add(host);

    return true;
  });
};

/** Whole seconds since the row appeared, ticking once a second while ``active``. */
const useElapsedSeconds = (active: boolean): number => {
  const [startedAt] = useState(() => Date.now());
  const [now, setNow] = useState(startedAt);

  useEffect(() => {
    if (!active) return undefined;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);

    return () => window.clearInterval(timer);
  }, [active]);

  return Math.floor((now - startedAt) / 1000);
};

const SiteChips: React.FC<{ sources: WebSearchSource[] }> = ({ sources }) => {
  const sites = siteSources(sources);
  const hidden = sites.length - MAX_SITES;

  return (
    <ul className="web-search__sites" aria-label="Sites this search read">
      {sites.slice(0, MAX_SITES).map((source) => (
        <li key={source.url}>
          <a
            className="web-search__site"
            href={source.url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            title={source.title || source.url}
          >
            {hostOf(source.url)}
          </a>
        </li>
      ))}
      {hidden > 0 && <li className="web-search__site web-search__site--more">+{hidden}</li>}
    </ul>
  );
};

/** One search: what is being searched (with a running timer), then the sites its answer came from. */
export const WebSearchRow: React.FC<{ search: WebSearchActivity }> = ({ search }) => {
  const searching = search.status === 'searching';
  const elapsed = useElapsedSeconds(searching);

  return (
    <div className={`web-search web-search--${search.status}`}>
      <div className="web-search__summary">
        <i className={`web-search__icon ${STATUS_ICON[search.status] ?? STATUS_ICON.done}`} aria-hidden="true" />
        <span className="web-search__label">{STATUS_LABEL[search.status] ?? STATUS_LABEL.done}</span>
        <q className="web-search__query">{search.query}</q>
        {searching && elapsed > 0 && <span className="web-search__meta">{elapsed}s</span>}
        {search.credits > 0 && <span className="web-search__meta">{formatCredits(search.credits)}</span>}
      </div>
      {search.sources?.length > 0 && <SiteChips sources={search.sources} />}
    </div>
  );
};

/** "2 searches running in parallel" while more than one is in flight. */
export const ParallelNote: React.FC<{ running: number; noun: string }> = ({ running, noun }) => (
  running < 2 ? null : (
    <p className="web-search-parallel">
      <i className="fa-solid fa-layer-group" aria-hidden="true" />
      {` ${running} ${noun} running in parallel`}
    </p>
  )
);

/** The web searches of a chat reply, above its text; nothing when it made none. */
export const WebSearchActivityList: React.FC<{ searches?: WebSearchActivity[] | null }> = ({ searches }) => {
  const items = (searches ?? []).filter((search) => search?.type === 'web_search' && search.query);

  if (!items.length) return null;

  return (
    <div className="web-search-list" aria-live="polite">
      <ParallelNote running={items.filter((search) => search.status === 'searching').length} noun="searches" />
      {items.map((search) => <WebSearchRow key={search.id} search={search} />)}
    </div>
  );
};
